// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A palm is a menu only near the face (14 Sep 2026).
//
// `wristMenu.ts` decides "palm up" from one number, the palm's dot with world up, and on the day
// the enter threshold came down to the posture people actually hold a menu on (0.55), the number
// began to say yes to a palm it should not have: the 18:58 session on a Quest 3 read `palmUp
// 0.59–0.81 | menu open` while the person was standing at the cabinet reaching down to press the
// credit button — a hand at machine level, half a metre below the eyes, that happened to turn
// palm-up on the way. The dot cannot tell that hand from a palm held up to be read; where the hand
// *is* can. A palm presented to be read is held near the face — a third to a half of a metre from
// the eyes — and a hand at a cabinet's front is never that close. So the shell asks the geometry
// before it asks the dot: within `PALM_READ_REACH` of the head the palm's number stands, and
// beyond it the palm reads as down, which closes a menu the hand carried away as well as refusing
// to open one.
//
// Two more things a palm has to be before it is a menu, from the 19:52 session the same day, both
// read off the hands line once it printed the palm's distance. **Open**: for thirty seconds of
// building, a left hand hanging as a relaxed fist — `L hands fist 56mm | palmUp 0.55 (0.57 m from
// the eyes)` — held the menu open over the bench; the panel is drawn on an open palm, and a fist
// is not one. `HandReport.closed`, the fingers-to-palm measure the carry already reads, says
// which. **Not at a control**: reaching for the start button on the cabinet's front door, the open
// left hand turned palm-up at 0.56–0.64 m from the eyes and the menu opened five times in eight
// seconds — "left-palm menu comes up when user is trying to press start button". Within reach,
// open, palm up: the dot and the distance cannot tell it from a menu. Where the *fingertip* is
// can: it is on the start button. A hand at a cabinet control is playing the cabinet
// (`cabinetControls.onAControl`, the test the nudge already defers to), and the shell asks that
// before it asks the palm.
//
// The same geometry answers a second question. Bring it to you is answered once the head has
// settled, and the first rule for "settled" was "level, within twenty degrees of the horizon" —
// wrong for a pinball player, who looks down at a playfield, and it held both of that session's
// answers to the three-second cap. What the rule wanted to exclude was a head still turned to the
// wrist the row was pressed on. `lookingAt` says exactly that: the head's forward within
// `PALM_LOOK_COS` of the direction to the palm, with the palm within reach.
//
// Pure: three points and a direction. The native client's hand layer would carry the same two
// tests in front of its `WristMenu.Advance`; they are the shell's, not the menu's, so they are not
// in the parity fixture.

export interface Point {
  readonly x: number
  readonly y: number
  readonly z: number
}

/**
 * How far from the eyes a palm may be and still be a menu, metres.
 *
 * 0.70. A palm held up to be read sits 0.30–0.50 m from the eyes, and one held low at the waist
 * while the head looks down at it about 0.63; a standing player's hand at the cabinet's front is
 * 0.9 m or more away (eyes at 1.6 m, a credit button at 0.7 m). A seated player's reach to that
 * button is nearer 0.6 m and is not caught by this number — the `[xr] hands` line prints the
 * distance, so the next seated session can say where the line should be.
 */
export const PALM_READ_REACH = 0.7

/** cos 30°: a head within thirty degrees of the palm is looking at it. */
export const PALM_LOOK_COS = Math.cos((30 * Math.PI) / 180)

/**
 * Is the hand open — fingers not curled to the palm — the way a palm to be read is?
 *
 * A measured fist is not; a hand nobody has measured is left to the palm's number, the same way
 * an unmeasured fist is read everywhere else (`HandReport.closedMeasured`).
 */
export function palmIsOpen(hand: { readonly closed: boolean; readonly closedMeasured: boolean }): boolean {
  return !(hand.closedMeasured && hand.closed)
}

/**
 * How squarely the palm faces the eyes: the palm's normal against the direction from the palm to
 * the head, as a cosine — 1 for a palm turned straight to the face, 0 edge-on, negative turned
 * away. `undefined` when either direction is degenerate.
 *
 * **The menu is the palm facing the player, not the palm facing the ceiling** (the 1.0.110
 * round, 27 Sep 2026: "the left-palm menu should always be facing user and should only disappear
 * when user no longer has left-palm facing user"). The dot with world up read a palm held up and
 * turned toward the face at 0.35–0.60, on the close line, and a palm lying flat at the waist the
 * same as one turned to be read. This number is what the owner asked for, and it goes to the
 * wrist menu's two thresholds in place of the old one.
 */
export function palmFacing(normal: Point, palm: Point, head: Point): number | undefined {
  const dx = head.x - palm.x
  const dy = head.y - palm.y
  const dz = head.z - palm.z
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  const n = Math.sqrt(normal.x * normal.x + normal.y * normal.y + normal.z * normal.z)
  if (d < 1e-6 || n < 1e-6) return undefined
  return (dx * normal.x + dy * normal.y + dz * normal.z) / (d * n)
}

/** Is the palm near enough to the eyes to be a menu? */
export function palmWithinReach(head: Point, palm: Point, reach = PALM_READ_REACH): boolean {
  return Math.hypot(palm.x - head.x, palm.y - head.y, palm.z - head.z) <= reach
}

/**
 * Is the head, facing `forward`, looking at `point`? `forward` need not be unit length.
 * A point under the head — no direction to it — is not being looked at.
 */
export function lookingAt(head: Point, forward: Point, point: Point, cosLimit = PALM_LOOK_COS): boolean {
  const dx = point.x - head.x
  const dy = point.y - head.y
  const dz = point.z - head.z
  const d = Math.hypot(dx, dy, dz)
  const f = Math.hypot(forward.x, forward.y, forward.z)
  if (d < 1e-6 || f < 1e-6) return false
  const cos = (dx * forward.x + dy * forward.y + dz * forward.z) / (d * f)
  return cos >= cosLimit
}
